import {
  Form,
  Row,
  Col,
  Input,
  Button,
  Select,
  DatePicker,
  Switch,
  InputNumber,
  Divider,
  Space,
  InputRef,
  Tooltip,
  message,
  Drawer,
  Grid,
} from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  MODAL_INITIAL_STATE,
  PROJECT_TEMPLATE_INITIAL_STATE,
  TASK_INITIAL_STATE,
} from "../../utils/data";
import {
  ProjectTemplateProps,
  TaskProps,
  UserProps,
} from "../../interfaces/interfaces";
import DataContext from "../../context/DataContext";
import dayjs from "dayjs";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { ModalCreateOrUpdateTask, TaskListTemplate } from "../task";
import { filterOption } from "../../utils/func/utils";

interface Props {
  modalStatus: {
    open: boolean;
    create: boolean;
    update: boolean;
    payload?: ProjectTemplateProps | null;
  };
  idClient: string;
  onCancel: () => void;
  onSubmit: (values: ProjectTemplateProps) => void;
  handleCreateProjectStatus: (
    values: ProjectTemplateProps,
    name: string,
  ) => void;
  handleEditProjectStatus: (idProjectStatus: string, next?: () => void) => void;
}

const statusOptions = [
  { value: "Active", label: "Active" },
  { value: "Completed", label: "Completed" },
  { value: "On Hold", label: "On Hold" },
];

const fieldTypes = [
  { value: "String", label: "String" },
  { value: "Number", label: "Number" },
  { value: "Date", label: "Date" },
  { value: "Boolean", label: "Boolean" },
];

export const ModalCreateOrUpdateProjectTemplate = ({
  modalStatus: { open, payload, create },
  onCancel,
  onSubmit,
  handleCreateProjectStatus,
  handleEditProjectStatus,
}: Props) => {
  const { useBreakpoint } = Grid;
  const { xs } = useBreakpoint();
  const [form] = Form.useForm();
  const [values, setValues] = useState<ProjectTemplateProps>(
    PROJECT_TEMPLATE_INITIAL_STATE,
  );
  const { usersList, userOptions } = useContext(DataContext);
  const [projectStatusName, setProjectStatusName] = useState("");
  const inputRefStatus = useRef<InputRef>(null);
  const [taskModal, setTaskModal] = useState<{
    open: boolean;
    create: boolean;
    update: boolean;
    payload: TaskProps | null;
    index?: number;
  } | null>(null);

  useEffect(() => {
    if (values) {
      form.setFieldsValue(values);
    } else {
      form.setFieldsValue(PROJECT_TEMPLATE_INITIAL_STATE);
      setValues(PROJECT_TEMPLATE_INITIAL_STATE);
    }
    setProjectStatusName("");
  }, [values]);

  useEffect(() => {
    if (payload) {
      setValues(payload);
    } else {
      setValues(PROJECT_TEMPLATE_INITIAL_STATE);
    }
    setProjectStatusName("");
  }, [payload]);

  const createProjectStatus = async () => {
    if (!projectStatusName.trim()) {
      message.error("Please enter an status name.");
      return;
    }
    try {
      handleCreateProjectStatus(values, projectStatusName);
      setProjectStatusName("");
    } catch (error) {
      message.error("Failed to create. Please try again.");
    }
  };

  return (
    <>
      <Drawer
        title="Project Template"
        open={open}
        width={xs ? "100vw" : 1100}
        onClose={onCancel}
        extra={[
          <Button type="default" onClick={onCancel}>
            Cancel
          </Button>,
          <Button
            type="primary"
            htmlType="submit"
            style={{ marginLeft: 8 }}
            onClick={() => {
              form.validateFields().then(() => {
                onSubmit(values);
                form.setFieldsValue(PROJECT_TEMPLATE_INITIAL_STATE);
                setValues(PROJECT_TEMPLATE_INITIAL_STATE);
              });
            }}
          >
            Save
          </Button>,
        ]}
        footer={[
          <Button type="default" onClick={onCancel}>
            Cancel
          </Button>,
          <Button
            type="primary"
            htmlType="submit"
            style={{ marginLeft: 8 }}
            onClick={() => {
              onSubmit(values);
              form.setFieldsValue(PROJECT_TEMPLATE_INITIAL_STATE);
              setValues(PROJECT_TEMPLATE_INITIAL_STATE);
            }}
          >
            Save Template
          </Button>,
        ]}
      >
        <Form
          layout="vertical"
          form={form}
          initialValues={values}
          onFinish={() => {}}
        >
          <Row gutter={16}>
            {/* Project Name */}
            <Col lg={8} md={12} sm={12} xs={12}>
              <Form.Item
                label="Template Name"
                name="name"
                rules={[
                  { required: true, message: "Project name is required" },
                ]}
              >
                <Input
                  placeholder="Enter template name"
                  value={values.name}
                  onChange={(event) =>
                    setValues((prev) => ({ ...prev, name: event.target.value }))
                  }
                />
              </Form.Item>
            </Col>

            {/* Status */}
            <Col lg={8} md={12} sm={12} xs={12}>
              <Form.Item label="Status" name="status">
                <Select
                  filterOption={filterOption}
                  showSearch
                  placeholder="Select project template status"
                  options={statusOptions}
                  value={values.status}
                  onChange={(newValue) =>
                    setValues((prev) => ({
                      ...prev,
                      status: newValue,
                    }))
                  }
                ></Select>
              </Form.Item>
            </Col>

            <Col lg={8} md={12} sm={12} xs={12}>
              <Form.Item label="Project Status List">
                <Select
                  filterOption={filterOption}
                  showSearch
                  placeholder="Select Status"
                  options={values.projectStatus?.map((status) => ({
                    label: status.name,
                    value: status._id,
                  }))}
                  dropdownRender={() => (
                    <>
                      <div>
                        {values.projectStatus?.map((status) => (
                          <div
                            key={status._id}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              padding: "4px 8px",
                            }}
                          >
                            <span>{status.name}</span>
                            <Button
                              type="text"
                              size="small"
                              danger
                              onClick={() => {
                                if (create) {
                                  message.error(
                                    "Cannot delete status in create mode.",
                                  );
                                  return;
                                }
                                handleEditProjectStatus(status._id, () => {
                                  setValues((prev) => ({
                                    ...prev,
                                    projectStatus: prev.projectStatus?.filter(
                                      (s) => s._id !== status._id,
                                    ),
                                  }));
                                });
                              }}
                              icon={<DeleteOutlined />}
                            ></Button>
                          </div>
                        ))}
                      </div>
                      <Divider style={{ margin: "8px 0" }} />
                      <Space style={{ padding: "0 8px 4px" }}>
                        <Input
                          disabled={create}
                          placeholder="New Status"
                          ref={inputRefStatus}
                          value={projectStatusName}
                          onChange={(event) =>
                            setProjectStatusName(event.target.value)
                          }
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                        <Tooltip title="Add New Status">
                          <Button
                            type="text"
                            icon={<PlusOutlined />}
                            onClick={createProjectStatus}
                          ></Button>
                        </Tooltip>
                      </Space>
                    </>
                  )}
                ></Select>
              </Form.Item>
            </Col>
            {/* Assigned To */}
            <Col lg={8} md={12} sm={12} xs={12}>
              <Form.Item label="Assigned To">
                <Select
                  showSearch
                  filterOption={filterOption}
                  allowClear
                  value={values.assignedTo?._id}
                  placeholder="Select user"
                  style={{ width: "100%" }}
                  options={userOptions}
                  onChange={(value) => {
                    const selected = usersList.find(
                      (user) => user._id === value,
                    );

                    setValues({
                      ...values,
                      assignedTo: (selected as UserProps) || null,
                    });
                  }}
                />
              </Form.Item>
            </Col>
            {/* Start Date */}
            <Col lg={7} md={7} sm={8} xs={8}>
              <Form.Item label="Start Date">
                <DatePicker
                  format="MM/DD/YYYY"
                  style={{ width: "100%" }}
                  value={values.startDate && dayjs(values.startDate)}
                  onChange={(date) =>
                    setValues((prev) => ({
                      ...prev,
                      startDate: date as any,
                    }))
                  }
                />
              </Form.Item>
            </Col>

            {/* End Date */}
            <Col lg={7} md={7} sm={8} xs={8}>
              <Form.Item label="End Date">
                <DatePicker
                  format="MM/DD/YYYY"
                  style={{ width: "100%" }}
                  value={values.endDate && dayjs(values.endDate)}
                  onChange={(date) =>
                    setValues((prev) => ({ ...prev, endDate: date as any }))
                  }
                />
              </Form.Item>
            </Col>
            <Col lg={2} md={8} sm={8} xs={8}>
              <Form.Item label="Active" name="active" valuePropName="checked">
                <Switch
                  disabled={create}
                  value={values.active}
                  onChange={(checked) =>
                    setValues((prev) => ({ ...prev, active: checked }))
                  }
                />
              </Form.Item>
            </Col>

            {/* Description */}
            <Col span={24}>
              <Form.Item label="Description" name="description">
                <Input.TextArea
                  rows={3}
                  placeholder="Enter project template description"
                  value={values.description}
                  onChange={(event) =>
                    setValues((prev) => ({
                      ...prev,
                      description: event.target.value,
                    }))
                  }
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <TaskListTemplate
                taskList={values.tasks || []}
                onAddNewTask={() =>
                  setTaskModal({
                    ...MODAL_INITIAL_STATE,
                    open: true,
                    create: true,
                    index: values.tasks?.length || 0,
                    payload: {
                      ...TASK_INITIAL_STATE,
                      origin: "project-template",
                    },
                  })
                }
                onEditTask={(task, index) => {
                  setTaskModal({
                    ...MODAL_INITIAL_STATE,
                    open: true,
                    update: true,
                    payload: { ...task, origin: "project-template" },
                    index,
                  });
                }}
                onDuplicateTask={(task) => {
                  setTaskModal({
                    ...MODAL_INITIAL_STATE,
                    open: true,
                    create: true,
                    update: false,
                    payload: {
                      ...task,
                      _id: "",
                      description: task.description + " (Copy)",
                      origin: "project-template",
                    },
                    index: values.tasks?.length,
                  });
                }}
                onDeleteTask={(_, index) => {
                  setValues((prev) => ({
                    ...prev,
                    tasks: prev.tasks?.filter((_, idx) => idx !== index),
                  }));
                }}
              />
            </Col>
          </Row>

          {/* Dynamic Custom Fields */}
          <Form.List name="customFields">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Row gutter={16} key={key} align="middle">
                    {/* Field Name */}
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "name"]}
                        rules={[
                          { required: true, message: "Field name is required" },
                        ]}
                      >
                        <Input placeholder="Field Name" />
                      </Form.Item>
                    </Col>

                    {/* Field Type */}
                    <Col span={6}>
                      <Form.Item
                        {...restField}
                        name={[name, "type"]}
                        rules={[{ required: true, message: "Select a type" }]}
                      >
                        <Select
                          filterOption={filterOption}
                          showSearch
                          placeholder="Field Type"
                          options={fieldTypes}
                        ></Select>
                      </Form.Item>
                    </Col>

                    {/* Field Value (Dynamic Based on Type) */}
                    <Col lg={8} md={12} sm={12} xs={12}>
                      <Form.Item shouldUpdate>
                        {({ getFieldValue }) => {
                          const fieldType = getFieldValue([
                            "customFields",
                            name,
                            "type",
                          ]);

                          if (fieldType === "String") {
                            return (
                              <Form.Item {...restField} name={[name, "value"]}>
                                <Input placeholder="Enter text" />
                              </Form.Item>
                            );
                          }

                          if (fieldType === "Boolean") {
                            return (
                              <Form.Item
                                {...restField}
                                name={[name, "value"]}
                                valuePropName="checked"
                              >
                                <Switch />
                              </Form.Item>
                            );
                          }

                          if (fieldType === "Date") {
                            return (
                              <Form.Item {...restField} name={[name, "value"]}>
                                <DatePicker style={{ width: "100%" }} />
                              </Form.Item>
                            );
                          }

                          if (fieldType === "Number") {
                            return (
                              <Form.Item {...restField} name={[name, "value"]}>
                                <InputNumber style={{ width: "100%" }} />
                              </Form.Item>
                            );
                          }

                          return null;
                        }}
                      </Form.Item>
                    </Col>

                    {/* Remove Field Button */}
                    <Col span={4}>
                      <Button type="link" danger onClick={() => remove(name)}>
                        Remove
                      </Button>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </Form.List>
        </Form>
      </Drawer>
      <ModalCreateOrUpdateTask
        modalStatus={taskModal}
        onCancel={() => {
          setTaskModal(MODAL_INITIAL_STATE);
        }}
        onSubmit={(values) => {
          if (taskModal?.update) {
            setValues((prev) => ({
              ...prev,
              tasks: prev.tasks?.map((task, index) =>
                taskModal.index === index ? values : task,
              ),
            }));
          } else if (taskModal?.create) {
            setValues((prev) => ({
              ...prev,
              tasks: [...(prev.tasks || []), values],
            }));
          }
          setTaskModal(MODAL_INITIAL_STATE);
        }}
      />
    </>
  );
};
